import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface LoginFormProps {
  initialError?: string;
  onClearError?: () => void;
}

export function LoginForm({ initialError, onClearError }: LoginFormProps = {}) {
  const [view, setView] = useState<'login' | 'reset' | 'success'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(
    initialError ? { type: 'error', text: initialError } : null
  );

  // Update message when initialError changes
  useEffect(() => {
    if (initialError) {
      setMessage({ type: 'error', text: initialError });
    }
  }, [initialError]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    onClearError?.();

    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: window.location.origin,
      });
      
      if (error) {
        // Don't reveal whether email exists or not
        console.error('Password reset error:', error);
      }
      
      // Show success view
      setView('success');
      
    } catch (error: any) {
      // For unexpected errors, show generic message
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (view === 'success') {
    return (
      <div className="text-center space-y-6">
        <div>
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">Check Your Email</h2>
          <p className="mt-2 text-gray-600 max-w-sm mx-auto">
            We've sent password reset instructions<br />to your email address.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Didn't receive an email? Check your spam folder<br />
            or contact{' '}
            <a href="mailto:admin@skip.community" className="text-blue-600 hover:text-blue-500">
              admin@skip.community
            </a>
          </p>
        </div>
        
        <button
          onClick={() => {
            setView('login');
            setResetEmail('');
            setMessage(null);
          }}
          className="text-blue-600 hover:text-blue-500 text-sm font-medium"
        >
          Return to sign in
        </button>
      </div>
    );
  }

  if (view === 'reset') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Set or Reset Your Password</h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your email address and we'll send you instructions
          </p>
        </div>

        <form onSubmit={handlePasswordReset} className="space-y-6">
          {message && (
            <div
              className={`p-3 rounded text-sm ${
                message.type === 'error'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-green-100 text-green-700'
              }`}
            >
              {message.text}
            </div>
          )}

          <div>
            <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="reset-email"
              type="email"
              required
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Your email address"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Instructions'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setView('login');
                setMessage(null);
              }}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Back to sign in
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSignIn} className="space-y-6">
        {message && (
          <div
            className={`p-3 rounded text-sm ${
              message.type === 'error'
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email address
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            placeholder="••••••••"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      <div className="border-t pt-6">
        <div className="text-center space-y-2">
          <button
            type="button"
            onClick={() => {
              setView('reset');
              setMessage(null);
            }}
            className="block w-full text-center py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            First time user? Set up your account
          </button>
          <button
            type="button"
            onClick={() => {
              setView('reset');
              setMessage(null);
            }}
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            Forgot your password?
          </button>
        </div>
      </div>
    </div>
  );
}